import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, AlertTriangle, ArrowLeft, Copy, Truck, MapPin, ExternalLink,
  CheckCircle2, Clock, FileText,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import OrderTimeline from "@/components/OrderTimeline";

const STATUS_META = {
  awaiting_payment:  { label: "Awaiting Payment",  tone: "yellow" },
  payment_submitted: { label: "Payment Submitted", tone: "blue"   },
  payment_verified:  { label: "Payment Confirmed", tone: "green"  },
  preparing:         { label: "Preparing Order",   tone: "green"  },
  ready:             { label: "Ready for Pickup",  tone: "green"  },
  shipped:           { label: "Shipped",           tone: "green"  },
  completed:         { label: "Completed",         tone: "green"  },
  cancelled:         { label: "Cancelled",         tone: "red"    },
};

const usdFmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function OrderDetail() {
  const { public_id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/collector-auth/orders/${public_id}`);
        if (alive) { setOrder(r.data); setError(null); }
      } catch (e) {
        if (alive) {
          setErrorStatus(e?.response?.status || null);
          setError(formatApiError(e?.response?.data?.detail) || "Unable to load this order.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [public_id]);

  if (loading) {
    return (
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-16 grid place-items-center text-[var(--smoke)]" data-testid="order-detail-loading">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  if (error || !order) {
    const notFound = errorStatus === 404;
    return (
      <div className="rounded-2xl border-2 border-[var(--panini-red)] bg-red-50 p-8 text-center" data-testid="order-detail-error">
        <AlertTriangle className="mx-auto text-[var(--panini-red)]" size={36} />
        <h2 className="font-display text-2xl mt-3">
          {notFound ? "We can't find that order." : "Something went wrong."}
        </h2>
        <p className="text-sm text-[var(--smoke)] mt-2 max-w-md mx-auto">
          {notFound
            ? "This order either doesn't exist or isn't attached to your account."
            : error}
        </p>
        <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/dashboard/orders" className="btn-ghost text-sm" data-testid="order-detail-back">
            <ArrowLeft size={14} /> Back to My Orders
          </Link>
        </div>
      </div>
    );
  }

  const meta = STATUS_META[order.status] || { label: order.status, tone: "gray" };
  const isPickup = (order.delivery?.code || "") === "local_pickup";
  const isShipping = String(order.delivery?.code || "").startsWith("ups");
  const totals = order?.pricing_snapshot?.totals || {};
  const lines = order?.pricing_snapshot?.lines || order?.requested_items || [];
  // Admin Quote Adjustments — when the quote → order conversion path
  // preserved an admin discount, the invoice must show it as a
  // separate line and the grand total must match the server-frozen
  // `pricing_snapshot.final_totals.final_total`. Falls back to legacy
  // `totals.total` for orders created without adjustments. Never
  // mutates line prices; the original sticker subtotal + tax rate
  // come straight from `revised_totals`.
  const adj = order?.pricing_snapshot?.adjustments || null;
  const finalTotals = order?.pricing_snapshot?.final_totals || null;
  const hasAdminDiscount = Boolean(
    (finalTotals && Number(finalTotals.discount_amount || 0) > 0)
    || (adj?.discount && adj.discount.type !== "none"
        && Number(adj.discount?.amount || adj.discount?.value || 0) > 0)
  );
  const discountLabel = adj?.discount?.type === "percentage"
    ? `Admin Discount (${Number(adj.discount.value || 0)}%)`
    : "Admin Discount";
  const discountAmount = Number(
    finalTotals?.discount_amount
    ?? adj?.discount?.amount
    ?? 0
  );
  const displayTotal = finalTotals?.final_total ?? totals.total;

  const copyOrderId = () => {
    navigator.clipboard.writeText(order.public_id);
    toast.success("Order ID copied");
  };

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate("/dashboard/orders")}
        data-testid="order-detail-back-link"
        className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--smoke)] hover:text-[var(--ink)]"
      >
        <ArrowLeft size={14} /> Back to My Orders
      </button>

      {/* Header */}
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[6px_6px_0_var(--ink)]" data-testid="order-detail-header">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="min-w-0">
            <div className="kicker kicker-red">Order Details</div>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl md:text-4xl tracking-tight" data-testid="order-detail-id">
                {order.public_id}
              </h1>
              <StatusPill meta={meta} testid="order-detail-status" />
            </div>
            <div className="mt-3 text-sm text-[var(--smoke)] grid sm:grid-cols-2 gap-x-6 gap-y-1">
              <div>
                <span className="text-[10px] uppercase tracking-widest font-bold">Order Date</span>
                <div className="text-[var(--ink)] font-bold" data-testid="order-detail-date">
                  {order.submitted_at ? new Date(order.submitted_at).toLocaleString() : "—"}
                </div>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-widest font-bold">Delivery</span>
                <div className="text-[var(--ink)] font-bold capitalize" data-testid="order-detail-delivery">
                  {String(order.delivery?.code || "unspecified").replace("_", " ")}
                </div>
              </div>
              <div>
                <span className="text-[10px] uppercase tracking-widest font-bold">Total</span>
                <div className="text-[var(--ink)] font-bold" data-testid="order-detail-total">
                  {usdFmt(totals.total)}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <button
              type="button"
              onClick={copyOrderId}
              data-testid="order-detail-copy-id"
              className="btn-ghost text-xs"
            >
              <Copy size={12} /> Copy Order ID
            </button>
          </div>
        </div>
      </div>

      {/* Timeline (shared component) */}
      <OrderTimeline order={order} />

      {/* Cancelled / Completed banners */}
      {order.status === "cancelled" && (
        <div className="p-5 border-2 border-red-900 bg-red-50 rounded-2xl text-red-900" data-testid="order-detail-cancelled">
          <p className="font-bold">This order has been cancelled.</p>
          <p className="text-sm mt-1">If this is unexpected, please reach out via our contact channels.</p>
        </div>
      )}
      {order.status === "completed" && (
        <div className="p-5 border-2 border-emerald-900 bg-emerald-50 rounded-2xl text-emerald-900" data-testid="order-detail-completed">
          <p className="font-bold">Your order has been completed. Thank you for ordering with CollectorUp.</p>
        </div>
      )}

      {/* Awaiting payment CTA — deep-link to public payment page */}
      {order.status === "awaiting_payment" && (
        <PaymentReminder order={order} />
      )}

      {/* Order Summary */}
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 md:p-8" data-testid="order-detail-summary">
        <h2 className="font-display text-2xl">Order summary</h2>
        <p className="text-xs text-[var(--smoke)] mt-1">
          Sticker count: <span className="font-bold text-[var(--ink)]">{totals.sticker_count ?? 0}</span> ·
          Prices frozen at time of order.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest border-b-2 border-[var(--ink)]">
                <th className="text-left py-2">Code</th>
                <th className="text-left py-2 hidden md:table-cell">Type</th>
                <th className="text-right py-2">Qty</th>
                <th className="text-right py-2">Unit</th>
                <th className="text-right py-2">Line</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={`${l.code}-${i}`} className="border-b border-[var(--stone)]" data-testid={`order-line-${l.code}`}>
                  <td className="py-2 font-mono font-bold">{l.code}</td>
                  <td className="py-2 text-xs text-[var(--smoke)] hidden md:table-cell">{l.matched_rule_label || "—"}</td>
                  <td className="py-2 text-right">{l.qty}</td>
                  <td className="py-2 text-right">{usdFmt(l.unit_price)}</td>
                  <td className="py-2 text-right font-bold">{usdFmt(l.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <TotalCell label="Subtotal" value={totals.subtotal} />
          {hasAdminDiscount && (
            <TotalCell
              label={discountLabel}
              value={-Math.abs(discountAmount)}
              testid="order-detail-admin-discount"
            />
          )}
          {totals.small_order_fee > 0 && <TotalCell label="Small-order fee" value={totals.small_order_fee} />}
          {totals.service_fee > 0 && <TotalCell label="Service fee" value={totals.service_fee} />}
          {totals.shipping > 0 && <TotalCell label="Shipping" value={totals.shipping} />}
          {totals.tax > 0 && <TotalCell label="Tax" value={totals.tax} />}
          <TotalCell label="Total" value={displayTotal} bold testid="order-detail-total-final" />
        </div>
      </div>

      {/* Shipment */}
      {isShipping && order.shipment?.tracking_number && (
        <div className="rounded-2xl border-2 border-emerald-900 bg-emerald-50 p-6" data-testid="order-detail-shipment">
          <div className="flex items-center gap-2 text-emerald-900">
            <Truck size={18} /> <div className="kicker kicker-red">Shipment</div>
          </div>
          <div className="mt-3 grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-900/80">Carrier</div>
              <div className="font-display text-xl">UPS</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-900/80">Tracking Number</div>
              <div className="font-mono font-bold text-lg" data-testid="order-detail-tracking">{order.shipment.tracking_number}</div>
            </div>
          </div>
          <a
            href={`https://www.ups.com/track?tracknum=${encodeURIComponent(order.shipment.tracking_number)}`}
            target="_blank" rel="noopener noreferrer"
            data-testid="order-detail-ups-btn"
            className="btn-red text-sm mt-4 inline-flex"
          >
            <ExternalLink size={14} /> Track on UPS
          </a>
        </div>
      )}

      {/* Pickup */}
      {isPickup && (
        <div className="rounded-2xl border-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)] p-6" data-testid="order-detail-pickup">
          <div className="flex items-center gap-2">
            <MapPin size={18} /> <div className="kicker kicker-black">Local pickup</div>
          </div>
          <div className="mt-3 font-display text-xl">Your Phone Plug</div>
          <div className="text-sm">20719 NW 2nd Ave<br />Miami, FL 33160</div>
          <div className="mt-3 text-sm text-[var(--ink)]/80" data-testid="order-detail-pickup-status">
            Current status: <span className="font-bold">{meta.label}</span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="rounded-2xl border-2 border-dashed border-[var(--ink)] bg-white p-5" data-testid="order-detail-actions">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Actions</div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button type="button" onClick={copyOrderId} className="btn-ghost text-sm" data-testid="order-detail-action-copy">
            <Copy size={13} /> Copy Order ID
          </button>
          {isShipping && order.shipment?.tracking_number && (
            <a
              href={`https://www.ups.com/track?tracknum=${encodeURIComponent(order.shipment.tracking_number)}`}
              target="_blank" rel="noopener noreferrer"
              className="btn-ghost text-sm"
              data-testid="order-detail-action-track"
            >
              <Truck size={13} /> Track Package
            </a>
          )}
          <span className="text-xs text-[var(--smoke)] inline-flex items-center gap-1" data-testid="order-detail-action-invoice-soon">
            <FileText size={13} /> View Invoice — coming soon
          </span>
        </div>
      </div>
    </div>
  );
}

function PaymentReminder({ order }) {
  const [busy, setBusy] = useState(false);

  const goToPayment = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/collector-auth/orders/${order.public_id}/tracker-link`);
      const path = r.data?.tracker_path;
      if (path) {
        // Navigate the browser to the tracker with the fresh magic token.
        window.location.href = path;
      } else {
        toast.error("Could not build a payment link. Please try again.");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not build a payment link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border-2 border-[var(--panini-red)] bg-[var(--panini-yellow-soft)] p-6 flex items-start gap-3" data-testid="order-detail-awaiting-payment">
      <Clock className="text-[var(--panini-red)] mt-0.5 shrink-0" size={22} />
      <div className="flex-1">
        <div className="font-bold text-[var(--ink)]">This order is waiting for payment.</div>
        <p className="text-sm text-[var(--smoke)] mt-1">
          Complete your Zelle payment on the secure order page to move forward.
        </p>
        <button
          type="button"
          onClick={goToPayment}
          disabled={busy}
          data-testid="order-detail-payment-cta"
          className="btn-red mt-3 inline-block text-sm"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          Go to payment page
        </button>
      </div>
    </div>
  );
}

function StatusPill({ meta, testid }) {
  const toneClasses = {
    yellow: "bg-[var(--panini-yellow)] text-[var(--ink)] border-[var(--ink)]",
    blue:   "bg-blue-50 text-blue-900 border-blue-900",
    green:  "bg-emerald-50 text-emerald-900 border-emerald-900",
    red:    "bg-red-50 text-red-900 border-red-900",
    gray:   "bg-neutral-100 text-[var(--ink)] border-[var(--ink)]",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border-2 font-bold text-xs uppercase tracking-widest ${toneClasses[meta.tone]}`}
    >
      <span className="w-2 h-2 rounded-full bg-current opacity-70" />
      {meta.label}
    </span>
  );
}

function TotalCell({ label, value, bold, testid }) {
  return (
    <div
      className={`p-3 rounded-lg border-2 border-[var(--ink)] ${bold ? "bg-[var(--panini-yellow)]" : "bg-white"}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold">{label}</div>
      <div className={`mt-1 ${bold ? "font-display text-xl" : "font-mono text-sm font-bold"}`}>
        {Number(value) < 0 ? `− ${usdFmt(Math.abs(value))}` : usdFmt(value)}
      </div>
    </div>
  );
}
